import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Copy, Crown, Loader2, Plug, Unplug } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { API_BASE } from "@/lib/api";
import { fmtRelative } from "@/lib/format";

// Settings → AI assistants. Shows the connector URL + setup steps, and lists connected AI apps
// (GET /api/oauth/connections) with a Disconnect action. See CLAUDE.md's "AI connector (MCP)".
const B = ({ children }) => <span className="font-medium text-slate-800">{children}</span>;
const APPS = [
    {
        id: "claude", label: "Claude", steps: [
            <>In Claude (web, desktop or mobile), open <B>Settings → Connectors</B> and choose <B>Add custom connector</B>.</>,
            <>Name it <B>BudgetWise</B> and paste the URL above.</>,
            <>Click <B>Connect</B>, sign in here and choose <B>Allow</B>.</>,
            <>In any chat, attach a screenshot and say <span className="italic">"add these to BudgetWise"</span>.</>,
        ],
    },
    {
        id: "chatgpt", label: "ChatGPT", steps: [
            <>On chatgpt.com, open <B>Plugins</B>, click <B>+</B> and choose <B>Add custom MCP server</B>.</>,
            <>Name it <B>BudgetWise</B>, paste the URL above as the HTTPS endpoint and choose <B>OAuth</B> for authentication.</>,
            <>Accept the warning, choose <B>Create as a plugin</B>, then sign in here and choose <B>Allow</B>.</>,
            <>In a chat, enable BudgetWise, attach a screenshot and ask ChatGPT to add it.</>,
        ],
    },
    {
        id: "gemini", label: "Gemini", steps: [
            <>On a computer, go to gemini.google.com → <B>Settings</B> → <B>Connected Apps</B> (under Personal Intelligence if you don't see it).</>,
            <>Under <B>Custom apps</B>, choose <B>Add a custom app</B> and paste the URL above. Leave the advanced fields empty.</>,
            <>Click <B>Next</B>, sign in here and choose <B>Allow</B>. It then works in the Gemini mobile app too.</>,
            <>In a chat, type <B>@</B>, pick BudgetWise, attach a screenshot and ask Gemini to add it.</>,
            <span className="text-xs text-slate-500">Gemini custom apps currently need a personal Google account in the US, English, age 18+, with Keep Activity on.</span>,
        ],
    },
];

export default function ConnectedApps({ canUse }) {
    const [app, setApp] = useState("claude");
    const [items, setItems] = useState(null);
    const [removing, setRemoving] = useState(null);
    const connectorUrl = API_BASE.startsWith("http") ? `${API_BASE}/mcp` : `${window.location.origin}${API_BASE}/mcp`;

    const load = () => api.get("/oauth/connections").then(({ data }) => setItems(data)).catch(() => setItems([]));
    useEffect(() => { load(); }, []);

    const copy = async () => {
        try { await navigator.clipboard.writeText(connectorUrl); toast.success("Connector URL copied"); }
        catch { toast.error("Couldn't copy — select the URL and copy it manually"); }
    };

    const disconnect = async () => {
        try {
            await api.delete(`/oauth/connections/${encodeURIComponent(removing.client_id)}`);
            toast.success(`${removing.client_name} disconnected`);
            load();
        } catch { toast.error("Failed to disconnect"); }
        finally { setRemoving(null); }
    };

    return (
        <div className="card-elev rounded-2xl p-6 space-y-5" data-testid="ai-assistants-section">
            <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400">
                    <Bot className="w-4 h-4" />
                </span>
                <div>
                    <h2 className="font-display text-base font-bold text-slate-900">AI assistants</h2>
                    <p className="text-xs text-slate-500">Add expenses by sharing a screenshot with Claude, ChatGPT or Gemini</p>
                </div>
            </div>

            {!canUse ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <div className="flex items-center gap-2 font-semibold"><Crown className="w-4 h-4 text-amber-500" /> Pro feature</div>
                    <p className="mt-1 text-amber-800">Connect Claude, ChatGPT or Gemini to add receipts, bills and statements just by sharing a screenshot.</p>
                    <Link to="/pricing" className="inline-block mt-3 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold">See plans</Link>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="text-sm font-semibold text-slate-800">Connector URL</div>
                    <div className="flex gap-2">
                        <code className="flex-1 min-w-0 truncate rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-700" data-testid="connector-url">{connectorUrl}</code>
                        <Button variant="outline" size="sm" onClick={copy} className="gap-1.5 shrink-0"><Copy className="w-3.5 h-3.5" /> Copy</Button>
                    </div>
                    <div className="flex gap-1 rounded-xl bg-slate-100 p-1 w-fit" role="tablist">
                        {APPS.map(a => (
                            <button key={a.id} type="button" role="tab" aria-selected={app === a.id} onClick={() => setApp(a.id)}
                                data-testid={`setup-tab-${a.id}`}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${app === a.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                                {a.label}
                            </button>
                        ))}
                    </div>
                    <ol className="text-sm text-slate-600 space-y-1.5 list-decimal pl-5" data-testid={`setup-steps-${app}`}>
                        {APPS.find(a => a.id === app).steps.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                </div>
            )}

            <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Connected apps</div>
                {items === null ? (
                    <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                ) : items.length === 0 ? (
                    <div className="text-sm text-slate-400 flex items-center gap-2"><Plug className="w-4 h-4" /> No AI apps connected yet</div>
                ) : items.map(c => (
                    <div key={c.client_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3" data-testid="connected-app">
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-800 truncate">{c.client_name}</div>
                            <div className="text-xs text-slate-500">
                                Connected {fmtRelative(c.connected_at)}{c.last_used_at ? ` · last used ${fmtRelative(c.last_used_at)}` : ""}
                            </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setRemoving(c)} className="gap-1.5 shrink-0 text-rose-600 border-rose-200 hover:bg-rose-50">
                            <Unplug className="w-3.5 h-3.5" /> Disconnect
                        </Button>
                    </div>
                ))}
            </div>

            <AlertDialog open={!!removing} onOpenChange={o => !o && setRemoving(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Disconnect {removing?.client_name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            It will immediately lose access to your BudgetWise data. Entries it already added stay in your ledger.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={disconnect} className="bg-rose-600 hover:bg-rose-700 text-white">Disconnect</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
